import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

import Autocomplete, { type AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';

import Avatar from '@mui/material/Avatar';
import useDebounceValue from 'hooks/useDebounceValue';
import { chatAllUsersByIdSelector } from 'domain/chat';
import { useTagSuggestions } from 'domain/documents/queries';

interface TagSuggestionsAutocompleteProps {
  inputValue: string;
  onInputChange: (value: string) => void;
  onSelect: (value: string) => void;
  requirePrefix?: string;
  prefixOutputWithHashtag?: boolean;
  fullWidth?: boolean;
  renderInput: (params: AutocompleteRenderInputParams) => React.ReactNode;
}

const USERS_GROUP = 'users';
const TAGS_GROUP = 'tags';

// Shared async tag/user suggestions engine — the modern replacement for the hand-rolled
// Popper + keyboard nav in legacy TagSuggestions. Owns options + grouping + the dense
// dropdown look; the input is per-consumer via `renderInput`. freeSolo: Enter on typed
// text confirms it verbatim, Enter on a highlighted option prefixes it (parity with the
// legacy onConfirm/stringToTag branch — no double `#`).
const TagSuggestionsAutocomplete = ({
  inputValue,
  onInputChange,
  onSelect,
  requirePrefix,
  prefixOutputWithHashtag = false,
  fullWidth = true,
  renderInput,
}: TagSuggestionsAutocompleteProps) => {
  const { formatMessage } = useIntl();
  const usersByID = useSelector(chatAllUsersByIdSelector);
  const [open, setOpen] = useState(false);

  // Only fetch + surface the dropdown in "suggestion mode" (input starts with the prefix);
  // free text drives the debounced grid search elsewhere and shows no dropdown here.
  const gated = !requirePrefix || inputValue.startsWith(requirePrefix);
  const debouncedPrefix = useDebounceValue(gated ? inputValue : '', 250);
  // v4 `isLoading` is `true` for a DISABLED query (never-fetched status), which would flash
  // "Loading…" on empty focus — use `isFetching` (real in-flight requests only).
  const { data: hints = [], isFetching } = useTagSuggestions(debouncedPrefix);

  // groupBy requires options pre-sorted by the group dimension, else duplicate headers.
  const options = useMemo(
    () => [...hints].sort((a, b) => Number(Boolean(usersByID.get(b))) - Number(Boolean(usersByID.get(a)))),
    [hints, usersByID],
  );

  const groupTitle = (group: string) =>
    formatMessage(
      group === USERS_GROUP
        ? { id: 'documents.tags.suggestions.dropdown.users', defaultMessage: 'Users' }
        : { id: 'documents.tags.suggestions.dropdown.tags', defaultMessage: 'Tags' },
    );

  return (
    <Autocomplete
      freeSolo
      autoHighlight
      fullWidth={fullWidth}
      sx={{ flexGrow: 1 }}
      // Drop the listbox's default 8px top padding so the sticky group header sits flush —
      // otherwise a scrolled row peeks through the padding band above the header.
      slotProps={{ listbox: { sx: { pt: 0 } } }}
      forcePopupIcon={false}
      // No MUI clear/popup buttons (each consumer renders its own end adornment). This also drops
      // the `hasClearIcon`/`hasPopupIcon` root classes that reserve a 39px right padding.
      disableClearable
      filterOptions={(x) => x}
      options={options}
      loading={isFetching}
      open={open && gated}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      // A selection becomes a chip (WS clears the input) — the Autocomplete must not retain a
      // "selected value", else on reopen it scrolls/highlights that stale option. Pin to empty.
      value=""
      inputValue={inputValue}
      onInputChange={(_e, value, reason) => {
        if (reason === 'input') onInputChange(value);
      }}
      onChange={(_e, value, reason) => {
        if (typeof value !== 'string') return;
        // createOption = raw typed text (already carries the `#`); selectOption = a bare
        // hint that must be prefixed — mirrors legacy onConfirm exactly (no double `#`).
        if (reason === 'createOption') onSelect(value);
        else if (reason === 'selectOption') onSelect(prefixOutputWithHashtag ? `#${value}` : value);
      }}
      groupBy={(option) => (usersByID.get(option) ? USERS_GROUP : TAGS_GROUP)}
      renderInput={renderInput}
      renderGroup={(params) => (
        <li key={params.key}>
          <ListSubheader sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="caption">{groupTitle(params.group)}</Typography>
          </ListSubheader>
          <ul style={{ padding: 0 }}>{params.children}</ul>
        </li>
      )}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        const user = usersByID.get(option);
        return (
          <MenuItem key={key} {...optionProps}>
            {user && (
              <ListItemIcon>
                <Avatar src={user.picture} sx={{ width: 24, height: 24 }} />
              </ListItemIcon>
            )}
            <ListItemText>
              <Typography variant="body2">{user ? `${user.username} (${option})` : option}</Typography>
            </ListItemText>
          </MenuItem>
        );
      }}
    />
  );
};

export default TagSuggestionsAutocomplete;
